import React, { ReactNode } from 'react';
import { MonitorIcon, SmartphoneIcon, TabletIcon } from 'lucide-react';
export type Device = 'desktop' | 'tablet' | 'mobile' | 'iphone16';

/** Anchos reales de viewport de cada dispositivo. */
const WIDTHS: Record<Device, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '390px',
  // iPhone 16 Pro Max: 440 × 956 puntos.
  iphone16: '440px'
};
const OPTIONS: {
  value: Device;
  label: string;
  icon: typeof MonitorIcon;
}[] = [{
  value: 'desktop',
  label: 'Escritorio',
  icon: MonitorIcon
}, {
  value: 'tablet',
  label: 'Tablet',
  icon: TabletIcon
}, {
  value: 'mobile',
  label: 'Móvil',
  icon: SmartphoneIcon
}];
interface DevicePreviewProps {
  device: Device;
  onDeviceChange: (device: Device) => void;
  children: ReactNode;
  toolbar?: ReactNode;
}

/**
 * Vista previa por dispositivo. Además de los tres anchos habituales incluye
 * una simulación de iPhone 16 Pro Max (440 × 956) con marco, isla dinámica e
 * indicador inferior, para revisar la landing tal y como la verá un visitante
 * desde el móvil.
 */
export function DevicePreview({
  device,
  onDeviceChange,
  children,
  toolbar
}: DevicePreviewProps) {
  const isPhoneFrame = device === 'iphone16';
  return <div className="flex h-full min-w-0 flex-col rounded-lg border border-border bg-surface" data-id="element-772" data-mp="iomyp1286co9">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2" data-id="element-773" data-mp="nnq62s5oguze">
        <div className="flex flex-wrap gap-1 rounded-md bg-background p-1" role="group" aria-label="Tamaño de la vista previa" data-id="element-774" data-mp="1bdki57i6afil">
          {OPTIONS.map(option => <button key={option.value} type="button" onClick={() => onDeviceChange(option.value)} aria-pressed={device === option.value} aria-label={option.label} title={option.label} className={`flex h-8 w-9 items-center justify-center rounded-sm transition-colors ${device === option.value ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface'}`} data-id="element-775" data-mp="11e1y6r29itqj">
              <option.icon className="h-4 w-4" data-id="element-776" data-mp="3r4outfk2d7k7" />
            </button>)}
          <button type="button" onClick={() => onDeviceChange('iphone16')} aria-pressed={isPhoneFrame} title="iPhone 16 Pro Max" className={`flex h-8 items-center justify-center gap-1.5 rounded-sm px-2.5 text-[11px] font-medium transition-colors ${isPhoneFrame ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-surface'}`} data-id="element-777" data-mp="3707s4q9ias7g">
            <SmartphoneIcon className="h-4 w-4" data-id="element-778" data-mp="2t7rte1nn1gcc" />
            iPhone 16 Pro Max
          </button>
        </div>
        {toolbar}
      </div>
      <div className="flex-1 overflow-auto p-4" data-id="element-779" data-mp="2jb6nzf7pi37z">
        {isPhoneFrame ?
      // Marco del teléfono: no recorta el contenido, solo lo enmarca.
      <div className="mx-auto w-full max-w-[472px]" data-id="element-780" data-mp="2fad3ywxdx8kd">
            <div className="relative mx-auto rounded-[3rem] border border-border bg-navy p-3 shadow-xl" data-id="element-781" data-mp="2abs26t2higtt">
              <div className="relative overflow-hidden rounded-[2.4rem] bg-background" data-id="element-782" data-mp="dizwq17y2ol5">
                <div className="pointer-events-none absolute left-1/2 top-2 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-navy" aria-hidden="true" data-id="element-783" data-mp="8j3rxzrfat4j" />
                <div className="h-[956px] max-h-[70vh] overflow-y-auto" style={{
              width: WIDTHS.iphone16,
              maxWidth: '100%'
            }} data-id="element-784" data-mp="g121235p0278">
                  {children}
                </div>
                <div className="pointer-events-none absolute bottom-2 left-1/2 z-20 h-1 w-28 -translate-x-1/2 rounded-full bg-navy/40" aria-hidden="true" data-id="element-785" data-mp="pvbw5sm0reln" />
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground" data-id="element-786" data-mp="3tc11jb9v8i9q">
              iPhone 16 Pro Max · 440 × 956 px
            </p>
          </div> : <div className="mx-auto overflow-hidden rounded-lg border border-border bg-background shadow-sm transition-all" style={{
        width: WIDTHS[device],
        maxWidth: '100%'
      }} data-id="element-787" data-mp="72860h80fjvs">
            {children}
          </div>}
      </div>
    </div>;
}